import {IWebsiteProductState} from "../reducers/website-product.reducer";
import {ITenWebProductState} from "../reducers/ten-web-product.reducer";
import {Product, ProductType} from "../../websites/website/product";
import {Config} from "../../config/config";
import {createFeatureSelector, createSelector} from "@ngrx/store";
import {ProductMapper} from "../../websites/website/product-mapper";
import {DbStoreState} from "../reducers";
import {Utils} from "../../shared/utils/utils";
import {TypeUtils} from "../../shared/utils/type-utils";

const getDbStoreState = createFeatureSelector<DbStoreState>('dbStore');
const getTenWebProductState = createSelector(getDbStoreState, (dbStoreState: DbStoreState) => dbStoreState.tenWebProduct);

export const getWebsiteProductState = createSelector(getDbStoreState, (dbStoreState: DbStoreState) => dbStoreState.websiteProduct);

export const getWebsitePlugins = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    return sortProductsByPopularRank(Object.values(getPluginsMap(websiteId, true, false, tenWebProductState, websiteProductState)));
  }
);

export const getWebsitePlugin = (websiteId: number, pluginSlug: string) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    return getPluginsMap(websiteId, true, false, tenWebProductState, websiteProductState)[pluginSlug];
  }
);

const getPluginsMap = (websiteId: number, includeWebsitePlugins: boolean, includeTenWebPlugins: boolean, tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState): {[slug: string]: Product} => {
  const tenWebPlugins: Product[] = Object.values(tenWebProductState.entities).filter((product: Product): boolean => {
    return product.type === ProductType.PLUGIN;
  });
  const tenWebPluginsMap: {[slug: string]: Product} = Utils.arrayToEntities(tenWebPlugins, 'slug');
  const websitePluginsMap: {[slug: string]: Product} = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] && websiteProductState.entities[websiteId].pluginEntities ? websiteProductState.entities[websiteId].pluginEntities : {};

  let slugs: string[] = [];
  if (includeWebsitePlugins) {
    slugs = slugs.concat(Object.keys(websitePluginsMap));
  }
  if (includeTenWebPlugins) {
    slugs = slugs.concat(Object.keys(tenWebPluginsMap));
  }
  slugs = Utils.unique(slugs);
  slugs = Utils.arrayDifference(slugs, Config.servicePluginSlugs.concat([Config.managerPluginSlug]));

  const pluginsMap: {string: Product} = slugs.reduce((pluginsMap, slug: string): {string: Product} => {
    const tenWebPlugin: any = tenWebPluginsMap[slug];
    const websitePlugin: any = websitePluginsMap[slug];
    const plugin: Product = tenWebPlugin ? ProductMapper.mapTenWebProduct(tenWebPlugin, websitePlugin) : ProductMapper.mapWordpressProduct(websitePlugin, ProductType.PLUGIN);

    pluginsMap[slug] = plugin;

    return pluginsMap;
  }, <{string: Product}>{});

  return pluginsMap;
};

const getExtensionsMap = (websiteId: number, includeWebsiteExtensions: boolean, includeTenWebExtensions: boolean, tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState): {[slug: string]: Product} => {
  const tenWebExtensions: Product[] = Object.values(tenWebProductState.entities).filter((product: Product): boolean => {
    return product.type === ProductType.ADD_ON;
  });
  const tenWebExtensionsMap: {[slug: string]: Product} = Utils.arrayToEntities(tenWebExtensions, 'slug');
  const websiteExtensionsMap: {[slug: string]: Product} = websiteProductState.entities[websiteId] && websiteProductState.entities[websiteId].extensionEntities ? websiteProductState.entities[websiteId].extensionEntities : {};

  let slugs: string[] = [];
  if (includeWebsiteExtensions) {
    slugs = slugs.concat(Object.keys(websiteExtensionsMap));
  }
  if (includeTenWebExtensions) {
    slugs = slugs.concat(Object.keys(tenWebExtensionsMap));
  }
  slugs = Utils.unique(slugs);

  const extensionsMap: {string: Product} = slugs.reduce((extensionsMap, slug: string): {string: Product} => {
    const tenWebExtension: any = tenWebExtensionsMap[slug];
    const websiteExtension: any = websiteExtensionsMap[slug];
    const extension: Product = tenWebExtension ? ProductMapper.mapTenWebProduct(tenWebExtension, websiteExtension) : ProductMapper.mapWordpressProduct(websiteExtension, ProductType.ADD_ON);

    extensionsMap[slug] = extension;

    return extensionsMap;
  }, <{string: Product}>{});

  return extensionsMap;
};


export const getWebsiteAndTenWebPluginExtensions = (websiteId: number, pluginId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    return Object.values(getExtensionsMap(websiteId, true, true, tenWebProductState, websiteProductState)).filter((extension: Product): boolean => {
      return extension.parentId === pluginId;
    });
  }
);

export const getWebsitePluginsAndExtensions = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    return Object.values(Object.values(getPluginsMap(websiteId, true, false, tenWebProductState, websiteProductState))).concat(Object.values(getExtensionsMap(websiteId, true, false, tenWebProductState, websiteProductState)));
  }
);

export const getTenWebPluginsAndExtensions = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    return Object.values(Object.values(getPluginsMap(websiteId, false, true, tenWebProductState, websiteProductState))).concat(Object.values(getExtensionsMap(websiteId, false, true, tenWebProductState, websiteProductState))).filter((product: Product): boolean => {
      return product.slug !== Config.builderPluginSlug;
    });
  }
);


export const getWebsiteThemes = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    return Object.values(getThemesMap(websiteId, true, false, tenWebProductState, websiteProductState));
  }
);



export const getWebsiteThemesLoaded = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsLoaded: boolean = tenWebProductState.loaded;
    const websiteThemesLoaded: boolean = websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].loaded : false;

    return tenWebProductsLoaded && websiteThemesLoaded;
  }
);

export const getWebsiteInactiveThemes = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    return Object.values(getThemesMap(websiteId, true, false, tenWebProductState, websiteProductState)).filter((theme: Product): boolean => !theme.active);
  }
);

export const getWebsiteActiveTheme = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    return Object.values(getThemesMap(websiteId, true, false, tenWebProductState, websiteProductState)).find((theme: Product): boolean => theme.active);
  }
);

export const getWebsiteTheme = (websiteId: number, themeSlug: string) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    return getThemesMap(websiteId, true, false, tenWebProductState, websiteProductState)[themeSlug];
  }
);

const getThemesMap = (websiteId: number, includeWebsiteThemes: boolean, includeTenWebThemes: boolean, tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState): {[slug: string]: Product} => {
  const tenWebThemes: Product[] = TypeUtils.isObject(tenWebProductState.entities) && Object.values(tenWebProductState.entities).filter((product: Product): boolean => {
    return product.type === ProductType.THEME;
  });
  const tenWebThemesMap: {[slug: string]: Product} = Utils.arrayToEntities(tenWebThemes, 'slug');
  const websiteThemesMap: {[slug: string]: Product} = websiteProductState.entities && websiteProductState.entities[websiteId] && websiteProductState.entities[websiteId].themeEntities ? websiteProductState.entities[websiteId].themeEntities : {};

  let slugs: string[] = [];
  if (includeWebsiteThemes) {
    slugs = slugs.concat(Object.keys(websiteThemesMap));
  }
  if (includeTenWebThemes) {
    slugs = slugs.concat(Object.keys(tenWebThemesMap));
  }
  slugs = Utils.unique(slugs);

  const themesMap: {string: Product} = slugs.reduce((themesMap, slug: string): {string: Product} => {
    const tenWebTheme: any = tenWebThemesMap[slug];
    const websiteTheme: any = websiteThemesMap[slug];
    const theme: Product = tenWebTheme ? ProductMapper.mapTenWebProduct(tenWebTheme, websiteTheme) : ProductMapper.mapWordpressProduct(websiteTheme, ProductType.THEME);

    themesMap[slug] = theme;

    return themesMap;
  }, <{string: Product}>{});

  return themesMap;
};


const sortProductsByPopularRank = (products: Product[]): Product[] => {
  return products.sort((p1: Product, p2: Product): number => {
    if (p2.popularRank > p1.popularRank) {
      return 1;
    }
    else if (p2.popularRank < p1.popularRank) {
      return -1;
    }

    return 0;
  });
};

export const getWebsitePluginsLoaded = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsLoaded: boolean = tenWebProductState.loaded;
    const websitePluginsLoaded: boolean = websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].loaded : false;
    return tenWebProductsLoaded && websitePluginsLoaded;
  }
);

export const getWebsiteManagerPlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsMap: {[slug: string]: any} = tenWebProductState.entities;
    const websitePluginsMap: {[slug: string]: any} = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteManagerPlugin: Product = tenWebProductsMap && websitePluginsMap && ProductMapper.mapTenWebProduct(tenWebProductsMap[Config.managerPluginSlug], websitePluginsMap[Config.managerPluginSlug]);

    return websiteManagerPlugin;
  }
);

export const getWebsiteBackupPlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsMap: {[slug: string]: any} = tenWebProductState.entities;
    const websitePluginsMap: {[slug: string]: any} = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteBackupPlugin: Product = tenWebProductsMap && tenWebProductsMap[Config.backupPluginSlug] ? ProductMapper.mapTenWebProduct(tenWebProductsMap[Config.backupPluginSlug], websitePluginsMap[Config.backupPluginSlug]) : null;

    return websiteBackupPlugin;
  }
);

export const getWebsiteImageOptimizerPlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsMap: {[slug: string]: any} = tenWebProductState.entities;
    const websitePluginsMap: {[slug: string]: any} = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteOptimizerPlugin: Product = tenWebProductsMap && tenWebProductsMap[Config.imageOptimizerPluginSlug] && websitePluginsMap ? ProductMapper.mapTenWebProduct(tenWebProductsMap[Config.imageOptimizerPluginSlug], websitePluginsMap[Config.imageOptimizerPluginSlug]) : null;

    return websiteOptimizerPlugin;
  }
);

export const getWebsiteOptimizerPlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsMap: {[slug: string]: any} = tenWebProductState.entities;
    const websitePluginsMap: {[slug: string]: any} = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteOptimizerPlugin: Product = tenWebProductsMap && tenWebProductsMap[Config.optimizerPluginSlug] && !TypeUtils.isEmpty(websitePluginsMap) ? ProductMapper.mapTenWebProduct(tenWebProductsMap[Config.optimizerPluginSlug], websitePluginsMap[Config.optimizerPluginSlug]) : null;

    return websiteOptimizerPlugin;
  }
);

export const getWebsiteSeoPlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsMap: { [slug: string]: any } = tenWebProductState.entities;
    const websitePluginsMap: { [slug: string]: any } = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteSeoPlugin: Product = tenWebProductsMap && tenWebProductsMap[Config.seoPluginSlug] ? ProductMapper.mapTenWebProduct(tenWebProductsMap[Config.seoPluginSlug], websitePluginsMap[Config.seoPluginSlug]) : null;

    return websiteSeoPlugin;
  }
);

export const getWebsiteSecurityPlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const tenWebProductsMap: { [slug: string]: any } = tenWebProductState.entities;
    const websitePluginsMap: { [slug: string]: any } = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteSecurityPlugin: Product = tenWebProductsMap && tenWebProductsMap[Config.securityPluginSlug] ? ProductMapper.mapTenWebProduct(tenWebProductsMap[Config.securityPluginSlug], websitePluginsMap[Config.securityPluginSlug]) : null;

    return websiteSecurityPlugin;
  }
);

export const getWebsiteWoocommercePlugin = (websiteId: number) => createSelector([getTenWebProductState, getWebsiteProductState],
  (tenWebProductState: ITenWebProductState, websiteProductState: IWebsiteProductState) => {
    const websitePluginsMap: { [slug: string]: any } = websiteProductState && websiteProductState.entities && websiteProductState.entities[websiteId] ? websiteProductState.entities[websiteId].pluginEntities : {};
    const websiteWoocommercePlugin: Product = websitePluginsMap[Config.woocommercePluginSlug] ? ProductMapper.mapWordpressProduct(websitePluginsMap[Config.woocommercePluginSlug], ProductType.PLUGIN) : null;
    return websiteWoocommercePlugin;
  }
);
